"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VerticalCalendar = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const lodash_1 = require("lodash");
const react_1 = require("react");
const constants_1 = require("../../constants");
const elements_1 = require("../../elements");
const translate_1 = require("../../translate");
const Wrapper = (0, styled_1.default)(elements_1.Flex)(() => ({
    overflow: 'auto',
    margin: `${constants_1.theme.spacing[2]}px auto`,
}));
const DaysWrapper = (0, styled_1.default)(elements_1.Flex)(() => ({
    width: '100%',
    overflow: 'auto',
    margin: `${constants_1.theme.spacing[2]}px auto`,
    gap: constants_1.theme.spacing[1],
    scrollSnapType: 'x mandatory',
    '&::-webkit-scrollbar': {
        // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
        display: 'none',
    },
}));
const Day = (0, styled_1.default)(elements_1.Clickable)(({ selected }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto',
    minWidth: '60px',
    width: `calc((100% - (7 * ${constants_1.theme.spacing[1]}px)) / 7)`,
    maxWidth: '80px',
    padding: constants_1.theme.spacing[2],
    scrollSnapAlign: 'none center',
    borderRadius: constants_1.theme.border.radius.normal,
    color: selected ? constants_1.Color.WHITE : constants_1.Color.BLACK,
    background: selected ? constants_1.Color.BLUE : constants_1.Color.GREY,
    textAlign: 'center',
}));
const getDateSignature = (date) => date.format('YYYY-MM-DD');
const calculateDates = (startDate, endDate) => {
    const items = [];
    let currentDate = startDate;
    while (currentDate) {
        if (currentDate.isAfter(endDate)) {
            break;
        }
        items.push({
            key: getDateSignature(currentDate),
            date: currentDate,
        });
        currentDate = currentDate.add(1, 'day');
    }
    return items;
};
const VerticalCalendar = ({ startDate, endDate, selectedDate, onDateSelected, }) => {
    const t = (0, translate_1.useTranslations)();
    const refs = (0, react_1.useRef)({});
    const dates = calculateDates(startDate, endDate);
    const [refsPrevLength, setRefsPrevLength] = (0, react_1.useState)(dates.length);
    const [minVisibleDate, setMinVisibleDate] = (0, react_1.useState)(startDate);
    const visibleRef = (0, react_1.useRef)(new Set());
    (0, react_1.useEffect)(() => {
        const ref = refs.current[getDateSignature(selectedDate)];
        window.requestAnimationFrame(() => ref?.scrollIntoView({ block: 'center' }));
    }, [selectedDate]);
    (0, react_1.useEffect)(() => {
        if (refsPrevLength > Object.keys(refs.current).length) {
            setRefsPrevLength(dates.length);
        }
    }, [refsPrevLength, dates.length]);
    (0, react_1.useEffect)(() => {
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    visibleRef.current.add(entry.target);
                }
                else {
                    visibleRef.current.delete(entry.target);
                }
            }
            const visibleItems = (0, lodash_1.sortBy)(Array.from(visibleRef.current.keys()), (element) => element.getAttribute('data-date'));
            if (visibleItems.length) {
                const attribute = visibleItems[0].getAttribute('data-date');
                if (attribute) {
                    const date = t.$date(attribute);
                    setMinVisibleDate(date);
                }
            }
        }, { threshold: [0] });
        Object.values(refs.current).forEach((ref) => {
            if (ref)
                observer.observe(ref);
        });
        return () => {
            Object.values(refs.current).forEach((ref) => {
                if (ref)
                    observer.unobserve(ref);
            });
        };
    }, [refsPrevLength]);
    return ((0, jsx_runtime_1.jsxs)(Wrapper, { col: true, rowGap: 1, children: [(0, jsx_runtime_1.jsx)(elements_1.Text, { color: constants_1.Color.DARK_GREY, children: minVisibleDate.format('MMMM') }), (0, jsx_runtime_1.jsx)(DaysWrapper, { children: dates.map(({ key, date }) => {
                    const selected = date.isSame(selectedDate);
                    return ((0, jsx_runtime_1.jsxs)(Day, { ref: (element) => {
                            refs.current[key] = element;
                        }, selected: selected, onClick: () => onDateSelected(date), "data-date": key, children: [(0, jsx_runtime_1.jsx)(elements_1.Text, { children: date.format('ddd').toUpperCase() }), (0, jsx_runtime_1.jsx)(elements_1.Text, { size: 2, children: date.date() })] }, key));
                }) })] }));
};
exports.VerticalCalendar = VerticalCalendar;
